import { useMemo } from "react"
import ReactSelect, {
  components as RSComponents,
  type GroupBase,
  type OptionProps,
  type SingleValueProps,
  type StylesConfig,
} from "react-select"
import { Lock } from "lucide-react"
import type {
  CustomFieldCatalog,
  CustomFieldLike,
  Scope,
} from "@/lib/predicate-ast"
import type { CustomFieldDefinition } from "@/types/custom_field"

// Field picker for one predicate row. Pinned "System fields" group
// surfaces reserved keys (`source_state`) at the top with a 🔒 + `sys`
// chip; live custom fields follow alphabetized.

type Option = {
  value: string  // `${scope}:${key}` — uniquely identifies a (scope, key) pair
  label: string
  fieldDef: CustomFieldLike & { label: string }
  reserved?: boolean
  scope: Scope
}

type Props = {
  catalog: CustomFieldCatalog
  scope: Scope
  fieldKey: string
  isSystem: boolean
  onChange: (selection: {
    scope: Scope
    fieldKey: string
    fieldDef: CustomFieldLike & { label: string }
    isSystem: boolean
  }) => void
  disabled?: boolean
}

// Styles mirror the dark-surface tokens used by `<Select>`. Inlined here
// because we need a 3-type-arg react-select signature for grouped options
// + custom `Option`/`SingleValue` overrides; the wrapper in select.tsx
// is single-arg.
const styles: StylesConfig<Option, false, GroupBase<Option>> = {
  control: (base, state) => ({
    ...base,
    minHeight: 38,
    backgroundColor: "var(--surface-panel-2, #1a1a1f)",
    borderColor: state.isFocused ? "var(--brand-red)" : "var(--border-soft, #2a2a30)",
    borderRadius: 8,
    boxShadow: state.isFocused ? "0 0 0 1px var(--brand-red)" : "none",
    cursor: "pointer",
    transition: "border-color 120ms ease",
    ":hover": { borderColor: "var(--border-strong, #3a3a42)" },
  }),
  valueContainer: (base) => ({ ...base, padding: "2px 10px" }),
  singleValue: (base) => ({ ...base, color: "var(--text-app, #e6e6ea)", fontSize: 14, fontWeight: 500 }),
  input: (base) => ({ ...base, color: "var(--text-app, #e6e6ea)" }),
  placeholder: (base) => ({ ...base, color: "var(--text-subtle, #6b6b75)" }),
  indicatorSeparator: () => ({ display: "none" }),
  menu: (base) => ({
    ...base,
    backgroundColor: "var(--surface-panel-2, #1a1a1f)",
    border: "1px solid var(--border-soft, #2a2a30)",
    borderRadius: 8,
    overflow: "hidden",
    boxShadow: "0 8px 24px rgba(0,0,0,0.4)",
    marginTop: 4,
    zIndex: 30,
  }),
  menuPortal: (base) => ({ ...base, zIndex: 50 }),
  menuList: (base) => ({ ...base, padding: 4 }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected
      ? "rgba(224, 69, 91, 0.18)"
      : state.isFocused
        ? "rgba(255,255,255,0.06)"
        : "transparent",
    color: "var(--text-app, #e6e6ea)",
    cursor: "pointer",
    fontSize: 13,
    padding: "6px 10px",
  }),
  groupHeading: (base) => ({
    ...base,
    color: "var(--text-subtle, #6b6b75)",
    fontSize: 10,
    letterSpacing: "0.05em",
    textTransform: "uppercase",
    padding: "4px 10px",
  }),
}

export function FieldPicker({ catalog, scope, fieldKey, isSystem, onChange, disabled }: Props) {
  const groups = useMemo(() => buildOptionGroups(catalog, scope), [catalog, scope])
  const all: Option[] = useMemo(() => groups.flatMap((g) => g.options), [groups])

  const wantedValue = `${scope}:${fieldKey}${isSystem ? ":sys" : ""}`
  const value = all.find((o) => o.value === wantedValue) ?? null

  return (
    <ReactSelect<Option, false, GroupBase<Option>>
      value={value}
      onChange={(o) => {
        if (!o) return
        onChange({
          scope: o.scope,
          fieldKey: o.fieldDef.key,
          fieldDef: o.fieldDef,
          isSystem: !!o.reserved,
        })
      }}
      options={groups}
      isDisabled={disabled}
      placeholder={all.length === 0 ? "No live fields yet" : "Pick a field…"}
      classNamePrefix="rs"
      styles={styles}
      menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}
      components={{ Option: FieldOption, SingleValue: FieldSingleValue }}
    />
  )
}

function FieldOption(props: OptionProps<Option, false, GroupBase<Option>>) {
  const { data } = props
  return (
    <RSComponents.Option {...props}>
      <div className="flex items-center gap-2">
        {data.reserved && <Lock size={11} className="text-app-muted" />}
        <span>{data.label}</span>
        {data.reserved && (
          <span className="ml-auto rounded-chip border border-border bg-white/5 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-app-muted">
            sys
          </span>
        )}
      </div>
    </RSComponents.Option>
  )
}

function FieldSingleValue(props: SingleValueProps<Option, false, GroupBase<Option>>) {
  const { data } = props
  return (
    <RSComponents.SingleValue {...props}>
      <div className="flex items-center gap-1.5">
        {data.reserved && <Lock size={11} className="text-app-muted" />}
        <span>{data.label}</span>
      </div>
    </RSComponents.SingleValue>
  )
}

function buildOptionGroups(
  catalog: CustomFieldCatalog,
  scope: Scope,
): GroupBase<Option>[] {
  const list = scope === "appuser" ? catalog.appuser : catalog.agent_convo

  const systemDefs = list.filter((d) => d.reserved)
  const liveCustoms = list
    .filter((d) => !d.reserved && d.status === "live")
    .sort((a, b) => a.label.localeCompare(b.label))

  const groups: GroupBase<Option>[] = []
  if (systemDefs.length > 0) {
    groups.push({
      label: "System fields",
      options: systemDefs.map((d) => systemOption(d, scope)),
    })
  }
  groups.push({
    label: "Custom fields",
    options: liveCustoms.map((d) => customOption(d, scope)),
  })
  return groups
}

function systemOption(d: CustomFieldDefinition, scope: Scope): Option {
  return {
    value: `${scope}:${d.key}:sys`,
    label: d.label,
    reserved: true,
    scope,
    fieldDef: {
      key:       d.key,
      label:     d.label,
      fieldType: "system_state",
      options:   d.options,
    },
  }
}

function customOption(d: CustomFieldDefinition, scope: Scope): Option {
  return {
    value: `${scope}:${d.key}`,
    label: d.label,
    scope,
    fieldDef: {
      key:       d.key,
      label:     d.label,
      fieldType: d.fieldType,
      options:   d.options,
    },
  }
}
